<template>
  <SfMultiselect
    v-model="selected"
    class="multiselect-single"
    :options="options"
    :mode="GROUP_PICKER_MODE"
    close-on-select
    :filter-results="false"
    :loading="searching"
    :placeholder="placeholder"
    :no-options-text="I18n.t('group.panel.no_search_results')"
    :no-results-text="I18n.t('group.panel.no_search_results')"
    :data-testid="testid"
    @search-change="onQuery"
  />
</template>

<script setup lang="ts" generic="T extends GroupPickerItem">
import type { Ref } from 'vue'
import type { GroupPickerItem } from '@app/application/user-management/types'

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import debounce from 'lodash/debounce'

import {
  GROUP_PICKER_MODE,
  GROUP_PICKER_SEARCH_DELAY,
  STALE_GROUP_PICKER_REQUEST_REASON,
} from '@app/application/user-management/constants'
import { abort, getSignal } from '@app/adapters/abort'
import useI18n from '@app/shared/vue/composables/use-I18n'
import SfMultiselect from '@app/shared/vue/components/sf-multiselect.vue'

defineOptions({ name: 'GroupPicker' })

// The server caps each answer, so the picker asks it again for every query
// rather than filtering one answer in the browser.
const props =
  defineProps<{
    testid: string
    placeholder: string
    excludeIds: number[]
    search: (query: string, signal?: AbortSignal) => Promise<T[] | null>
    requestKey: Parameters<typeof abort>[0]
  }>()

const emit = defineEmits<{ select: [item: T] }>()

const I18n = useI18n()

const selected = ref<number | null>(null)
const results = ref<T[]>([]) as Ref<T[]>
const searching = ref(false)

const candidates = computed(() =>
  results.value.filter((item) => !props.excludeIds.includes(item.id))
)

const options = computed(() =>
  candidates.value.map((item) => ({ value: item.id, label: item.name }))
)

function abortSearch(): void {
  abort(props.requestKey, STALE_GROUP_PICKER_REQUEST_REASON)
}

async function runSearch(query: string): Promise<void> {
  const signal = getSignal(props.requestKey)

  searching.value = true

  try {
    const found = await props.search(query, signal)

    if (!signal?.aborted) results.value = found ?? []
  } catch {
    if (!signal?.aborted) results.value = []
  } finally {
    if (!signal?.aborted) searching.value = false
  }
}

const searchLater = debounce((query: string) => {
  void runSearch(query)
}, GROUP_PICKER_SEARCH_DELAY)

// The options on screen answer the previous query. They go as soon as the
// query changes, so a click cannot land on a row the next answer replaces.
function onQuery(query: string): void {
  abortSearch()
  results.value = []
  searching.value = true
  searchLater(query)
}

// The field is a way to add a row, not a value the panel keeps: emptying it
// again both clears the label and lets the same option be picked twice if the
// caller puts it back on the list.
watch(selected, (id) => {
  if (id === null) return

  const item = candidates.value.find((candidate) => candidate.id === id)

  selected.value = null

  if (item) emit('select', item)
})

onMounted(() => {
  abortSearch()
  void runSearch('')
})

onBeforeUnmount(() => {
  searchLater.cancel()
  abortSearch()
})
</script>
